import React, { useState, useEffect } from 'react';
import {
  Table, Button, Modal, Input, Select, Tag, Space,
  message, Tooltip, Popconfirm, Typography, Card, Row, Col,
  Switch, InputNumber
} from 'antd';
import {
  PlusOutlined, DeleteOutlined, SettingOutlined,
  ShopOutlined, TeamOutlined, CheckCircleOutlined
} from '@ant-design/icons';
import { useAuth } from '../../utils/auth';
import storeService from '../../services/storeService';
import weworkService from '../../services/weworkService';

const { Option } = Select;
const { Text } = Typography;

/**
 * 门店群活码配置表格组件
 * 用于批量群活码创建时的门店选择和参数配置
 */
const StoreGroupConfigTable = ({ value = [], onChange, loading, lbsContactWayName = '', disabledStoreIds = [] }) => {
  const { token } = useAuth();
  const [storeModalVisible, setStoreModalVisible] = useState(false);
  const [stores, setStores] = useState([]);
  const [groups, setGroups] = useState([]);
  const [storesLoading, setStoresLoading] = useState(false);
  const [groupsLoading, setGroupsLoading] = useState(false);

  // 初始化数据
  useEffect(() => {
    fetchStores();
    fetchGroups();
  }, []);

  // 当LBS活码名称改变时，自动更新所有门店的活码名称
  useEffect(() => {
    if (value.length > 0) {
      const updatedConfigs = value.map(config => ({
        ...config,
        contactWayName: lbsContactWayName 
          ? `${lbsContactWayName}-${config.storeName}` 
          : `${config.storeName}群活码`
      }));
      onChange(updatedConfigs);
    }
  }, [lbsContactWayName]);

  const fetchStores = async () => {
    try {
      setStoresLoading(true);
      const response = await storeService.getStores({ page: 0, size: 1000 });
      console.log('门店数据响应:', response);
      setStores(response.content || []);
    } catch (error) {
      console.error('获取门店列表失败:', error);
      message.error('获取门店列表失败');
    } finally {
      setStoresLoading(false);
    }
  };

  const fetchGroups = async () => {
    try {
      setGroupsLoading(true);
      const groupsList = await weworkService.getAllGroups();
      console.log('企微群数据响应:', groupsList);
      setGroups(groupsList || []);
    } catch (error) {
      console.error('获取企微群列表失败:', error);
      message.error('获取企微群列表失败');
    } finally {
      setGroupsLoading(false);
    }
  };

  /**
   * 更新配置项
   */
  const updateConfig = (index, field, newValue) => {
    const newConfigs = [...value];
    newConfigs[index] = { ...newConfigs[index], [field]: newValue };
    onChange(newConfigs);
  };

  // 表格列定义
  const columns = [
    {
      title: '门店名称',
      dataIndex: 'storeName',
      width: 160,
      fixed: 'left',
      render: (text, record) => (
        <Space>
          <ShopOutlined />
          <Text strong>{text}</Text>
        </Space>
      )
    },
    {
      title: '群活码名称',
      dataIndex: 'contactWayName',
      width: 200,
      render: (text, record, index) => (
        <Input
          placeholder="请输入群活码名称（用于内部管理）"
          value={text}
          onChange={(e) => updateConfig(index, 'contactWayName', e.target.value)}
          size="middle"
          maxLength={30}
          showCount
        />
      )
    },
    {
      title: '选择群聊',
      dataIndex: 'chatIdList',
      width: 280,
      render: (chatIdList, record, index) => (
        <Select
          mode="multiple"
          placeholder="请选择群聊（最多5个）"
          value={chatIdList || []}
          onChange={(values) => {
            const limitedValues = values.slice(0, 5);
            
            // 批量更新状态，避免多次render导致的状态冲突
            const newConfigs = [...value];
            const updatedConfig = { 
              ...newConfigs[index], 
              chatIdList: limitedValues 
            };
            
            // 自动填充群名前缀：取第一个选中群聊的名称（仅当开启自动建群且群名前缀为空时）
            if (limitedValues.length > 0 && record.autoCreateRoom && (!record.roomBaseName || record.roomBaseName.trim() === '')) {
              const firstSelectedGroup = groups.find(group => 
                (group.chatId || group.id) === limitedValues[0]
              );
              if (firstSelectedGroup && firstSelectedGroup.name) {
                // 从群名中提取合适的前缀（去掉数字和特殊符号）
                let groupNamePrefix = firstSelectedGroup.name
                  .replace(/[0-9]+$/, '')  // 去掉末尾数字
                  .replace(/[一二三四五六七八九十]+$/, '') // 去掉末尾中文数字
                  .replace(/群$/, '')      // 去掉末尾的"群"字
                  .replace(/[（）()]/g, '')  // 去掉括号
                  .trim();
                
                if (groupNamePrefix && groupNamePrefix.length > 0) {
                  updatedConfig.roomBaseName = groupNamePrefix;
                }
              }
            }
            
            newConfigs[index] = updatedConfig;
            onChange(newConfigs);
          }}
          size="middle"
          style={{ width: '100%' }}
          showSearch
          loading={groupsLoading}
          filterOption={(input, option) => {
            const label = option?.label || option?.children || '';
            return label.toLowerCase().indexOf(input.toLowerCase()) >= 0;
          }}
          maxTagCount={2}
          maxTagTextLength={8}
        >
          {groups.map(group => (
            <Option 
              key={group.chatId || group.id} 
              value={group.chatId || group.id}
              label={group.name}
            >
              <Space>
                <TeamOutlined />
                <Text ellipsis style={{ maxWidth: '200px' }}>{group.name}</Text>
                {group.ownerName && (
                  <Text type="secondary" style={{ fontSize: '12px' }}>
                    (群主: {group.ownerName})
                  </Text>
                )}
              </Space>
            </Option>
          ))}
        </Select>
      )
    },
    {
      title: '自动建群',
      dataIndex: 'autoCreateRoom',
      width: 100,
      render: (autoCreateRoom, record, index) => (
        <Switch
          checked={autoCreateRoom === true}
          size="middle"
          onChange={(checked) => {
            // 批量更新状态，避免多次render
            const newConfigs = [...value];
            const updatedConfig = { 
              ...newConfigs[index], 
              autoCreateRoom: checked 
            };
            
            // 如果关闭自动建群，清空相关字段
            if (!checked) {
              updatedConfig.roomBaseName = '';
              updatedConfig.roomBaseId = 1;
            }
            
            newConfigs[index] = updatedConfig;
            onChange(newConfigs);
          }}
        />
      )
    },
    {
      title: '群名前缀',
      dataIndex: 'roomBaseName',
      width: 140,
      render: (roomBaseName, record, index) => (
        <Input
          placeholder="群名前缀"
          value={roomBaseName || ''}
          onChange={(e) => updateConfig(index, 'roomBaseName', e.target.value)}
          size="middle"
          maxLength={40}
          disabled={!record.autoCreateRoom}
          style={{ opacity: record.autoCreateRoom ? 1 : 0.5 }}
        />
      )
    },
    {
      title: '起始序号',
      dataIndex: 'roomBaseId',
      width: 100,
      render: (roomBaseId, record, index) => (
        <InputNumber
          placeholder="1"
          value={roomBaseId || 1}
          onChange={(value) => updateConfig(index, 'roomBaseId', value || 1)}
          size="middle"
          min={1}
          max={9999}
          disabled={!record.autoCreateRoom}
          style={{ width: '80px', opacity: record.autoCreateRoom ? 1 : 0.5 }}
        />
      )
    },
    {
      title: '备注',
      dataIndex: 'remark',
      width: 150,
      render: (remark, record, index) => (
        <Input
          placeholder="备注信息"
          value={remark || ''}
          onChange={(e) => updateConfig(index, 'remark', e.target.value)}
          size="middle"
          maxLength={200}
        />
      )
    },
    {
      title: '操作',
      width: 80,
      fixed: 'right',
      render: (_, record, index) => (
        <Popconfirm
          title="确定要删除这个门店吗？"
          onConfirm={() => handleDelete(index)}
        >
          <Button
            type="text"
            danger
            icon={<DeleteOutlined />}
            size="middle"
          >
            删除
          </Button>
        </Popconfirm>
      )
    }
  ];

  /**
   * 添加门店
   */
  const handleAddStore = () => {
    setStoreModalVisible(true);
  };

  /**
   * 选择门店后的配置
   */
  const handleStoreSelected = (selectedStores) => {
    const newConfigs = selectedStores.map(store => {
      // 生成群活码名称，如果有LBS活码名称前缀则使用，否则使用默认格式
      const contactWayName = lbsContactWayName 
        ? `${lbsContactWayName}-${store.name}` 
        : `${store.name}群活码`;
      
      return {
        storeId: store.id,
        storeName: store.name,
        contactWayName, // 群活码名称
        chatIdList: [], // 群聊ID列表，最多5个
        autoCreateRoom: true, // 默认开启自动建群
        roomBaseName: '', // 群名前缀，当autoCreateRoom为true时有效
        roomBaseId: 1, // 群起始序号，当autoCreateRoom为true时有效
        remark: '' // 备注信息
      };
    });
    
    // 过滤已存在的门店
    const existingStoreIds = value.map(config => config.storeId);
    const filteredConfigs = newConfigs.filter(config => 
      !existingStoreIds.includes(config.storeId)
    );
    
    if (filteredConfigs.length === 0) {
      message.warning('选择的门店已存在');
      return;
    }
    
    onChange([...value, ...filteredConfigs]);
    message.success(`已添加 ${filteredConfigs.length} 个门店`);
    setStoreModalVisible(false);
  };

  /**
   * 删除门店配置
   */
  const handleDelete = (index) => {
    const newValue = [...value];
    newValue.splice(index, 1);
    onChange(newValue);
    message.success('删除成功');
  };

  return (
    <div>
      {/* 操作栏 */}
      <div style={{ marginBottom: 16 }}>
        <Space>
          <Button 
            type="primary" 
            icon={<PlusOutlined />} 
            onClick={handleAddStore}
          >
            添加门店
          </Button>
          {value.length > 0 && (
            <Text type="secondary">
              已配置 {value.length} 个门店
            </Text>
          )}
        </Space>
      </div>

      {/* 门店配置表格 */}
      <Table
        columns={columns}
        dataSource={value}
        rowKey={(record, index) => `${record.storeId}_${index}`}
        pagination={false}
        size="middle"
        loading={loading}
        scroll={{ x: 1400 }}
        rowClassName={() => 'store-group-config-row'}
        locale={{
          emptyText: (
            <div style={{ padding: '40px 0', textAlign: 'center' }}>
              <TeamOutlined style={{ fontSize: 48, color: '#d9d9d9' }} />
              <p style={{ color: '#999', marginTop: 16 }}>
                请点击"添加门店"选择需要创建群活码的门店
              </p>
              <p style={{ color: '#999', fontSize: 12 }}>
                添加门店后可直接在表格中编辑群活码名称、选择群聊等参数
              </p>
            </div>
          )
        }}
      />

      {/* 自定义样式 */}
      <style jsx>{`
        :global(.store-group-config-row) {
          height: 80px;
        }
        :global(.store-group-config-row .ant-table-cell) {
          padding: 8px 6px !important;
          vertical-align: middle;
        }
        :global(.store-group-config-row .ant-select) {
          min-height: 32px;
        }
        :global(.store-group-config-row .ant-input) {
          height: 32px;
        }
      `}</style>

      {/* 门店选择弹窗 - 复用StoreSelector组件 */}
      <Modal
        title="选择门店"
        open={storeModalVisible}
        onCancel={() => setStoreModalVisible(false)}
        width={800}
        footer={null}
      >
        <StoreSelector 
          stores={stores}
          loading={storesLoading}
          onSelect={handleStoreSelected}
          selectedStoreIds={value.map(config => config.storeId)}
          disabledStoreIds={disabledStoreIds}
        />
      </Modal>
    </div>
  );
};

/**
 * 门店选择器组件（复用）
 */
const StoreSelector = ({ stores, loading, onSelect, selectedStoreIds, disabledStoreIds = [] }) => {
  const [selectedRows, setSelectedRows] = useState([]);
  const [searchText, setSearchText] = useState('');

  // 过滤门店
  const filteredStores = stores.filter(store =>
    store.name.toLowerCase().includes(searchText.toLowerCase()) ||
    store.address?.toLowerCase().includes(searchText.toLowerCase())
  );

  // 表格列定义
  const columns = [
    {
      title: '门店名称',
      dataIndex: 'name',
      render: (text, record) => (
        <Space>
          <ShopOutlined />
          <div>
            <div>{text}</div>
            {record.address && (
              <Text type="secondary" style={{ fontSize: '12px' }}>
                {record.address}
              </Text>
            )}
          </div>
        </Space>
      )
    },
    // {
    //   title: '服务半径',
    //   dataIndex: 'serviceRadius',
    //   width: 120,
    //   render: (radius) => radius ? `${radius}m` : '-'
    // },
    {
      title: '状态',
      dataIndex: 'status',
      width: 100,
      render: (status) => (
        <Tag color={status === 'active' ? 'green' : 'default'}>
          {status === 'active' ? '启用' : '停用'}
        </Tag>
      )
    }
  ];

  const preselectedDisabledIds = Array.from(new Set([...(selectedStoreIds || []), ...(disabledStoreIds || [])]));
  const allSelectedRowKeys = Array.from(new Set([...preselectedDisabledIds, ...selectedRows.map(row => row.id)]));

  const rowSelection = {
    selectedRowKeys: allSelectedRowKeys,
    onChange: (selectedRowKeys, selectedRowsParam) => {
      const activeRows = selectedRowsParam.filter(row => !preselectedDisabledIds.includes(row.id));
      setSelectedRows(activeRows);
    },
    getCheckboxProps: (record) => ({
      disabled: preselectedDisabledIds.includes(record.id),
      name: record.name,
    }),
  };

  return (
    <div>
      <div style={{ marginBottom: 16 }}>
        <Input.Search
          placeholder="搜索门店名称或地址"
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          style={{ width: 300 }}
        />
      </div>

      <Table
        rowSelection={rowSelection}
        columns={columns}
        dataSource={filteredStores}
        rowKey="id"
        loading={loading}
        pagination={{ pageSize: 100 }}
        size="middle"
      />

      <div style={{ marginTop: 16, textAlign: 'right' }}>
        <Space>
          <Text>已选择 {selectedRows.length} 个门店</Text>
          <Button
            type="primary"
            onClick={() => onSelect(selectedRows)}
            disabled={selectedRows.length === 0}
          >
            确认添加
          </Button>
        </Space>
      </div>
    </div>
  );
};

export default StoreGroupConfigTable;